import type { Ref } from 'vue';
import type { SpeakerDetailDTO } from '~/shared/api/client/events/events.dto';
import {
  OG_SITE_NAME,
  getAbsoluteUrl,
  getOgImageMeta,
  stripHtml,
} from '~/shared/lib/seo';
import { useOrigin } from '~/shared/lib/useOrigin';
import OgImage from '~/shared/images/og-image.png';

const OG_TYPE = 'profile';
const FALLBACK_TITLE = 'Спикер мероприятий';

export function useSpeakerDetailSeo(
  speaker: Ref<SpeakerDetailDTO | null | undefined>,
) {
  const route = useRoute();
  const origin = useOrigin();

  useHead(() => {
    const speakerData = speaker.value;
    const url = `${origin}${route.fullPath}`;
    const title = speakerData?.name || FALLBACK_TITLE;
    const description =
      stripHtml(speakerData?.description) || speakerData?.position || title;
    const image =
      getAbsoluteUrl(origin, speakerData?.photo?.file_url) ||
      getAbsoluteUrl(origin, OgImage);

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:type', content: OG_TYPE },
        { property: 'og:url', content: url },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:site_name', content: OG_SITE_NAME },
        ...getOgImageMeta(origin, speakerData?.photo?.file_url),
      ],
      script: speakerData
        ? [
            {
              type: 'application/ld+json',
              key: 'ld-json-speaker',
              innerHTML: {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Person',
                    name: speakerData.name,
                    jobTitle: speakerData.position,
                    description,
                    image,
                    url,
                    worksFor: speakerData.company
                      ? {
                          '@type': 'Organization',
                          name: speakerData.company.name,
                          url: speakerData.company.site || undefined,
                        }
                      : undefined,
                  },
                  {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Главная',
                        item: origin,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'Мероприятия',
                        item: `${origin}/events`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 3,
                        name: speakerData.name,
                        item: url,
                      },
                    ],
                  },
                ],
              },
            },
          ]
        : [],
    };
  });
}
